import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  Autocomplete,
  Paper,
  Fade,
  Stack,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useEffect, useState, memo } from 'react';
import { useInView } from 'react-intersection-observer';
import CircleIcon from '@mui/icons-material/Circle';
// ✅ Import the utility
import { encodeId } from '../utils/idEncoder';

type Dealer = {
  dealerCompanyId: number;
  dealerCompanyName: string;
  imageUrl: string;
  dealerPackageId: number;
  packageName: string;
  showDealerInVehicleList: boolean;
  showDealerCompanyLogo: boolean;
};

type Props = {
  dealerData: Dealer[];
  title?: string;
  adSpaceList?: any[];
};

// --- 1. DEALER CARD ---
const DealerCard = ({ dealer }: { dealer: Dealer }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={dealer.dealerCompanyId}>
      <Fade in={inView} timeout={500}>
        <Paper
          ref={ref}
          component={RouterLink}
          // ✅ ENCODED: Converts id 15 -> "/dealer-vehicles/X9k2m"
          to={`/dealer-vehicles/${encodeId(dealer.dealerCompanyId)}`}
          elevation={0}
          sx={{
            width: '100%',
            height: 110,
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textDecoration: 'none',
            bgcolor: '#fff',
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
            boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
            '&:hover': {
              transform: 'translateY(-6px)',
              boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
              borderColor: 'rgba(0,0,0,0.05)',
            },
          }}
        >
          {inView ? (
            <Box
              component="img"
              src={dealer.imageUrl}
              alt={dealer.dealerCompanyName}
              sx={{
                maxWidth: '70%',
                maxHeight: '55%',
                objectFit: 'contain',
                filter: 'grayscale(20%)',
                opacity: 0.85,
                transition: 'all 0.3s ease',
                '&:hover': {
                  filter: 'grayscale(0%)',
                  opacity: 1,
                  transform: 'scale(1.05)'
                }
              }}
              loading="lazy"
            />
          ) : null}
        </Paper>
      </Fade>
    </Grid>
  );
};

// --- 2. AD COMPONENT ---
const DealerSideAd = ({ ads }: { ads: any[] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (ads.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % ads.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [ads]);

  if (!ads || ads.length === 0) return null;

  const currentAd = ads[currentIndex];

  return (
    <Box sx={{ position: 'relative', width: '100%', perspective: '1000px' }}>
      <Fade key={currentAd.adSpaceImageId} in={true} timeout={800}>
        <Box
          component="a"
          href={currentAd.adSpaceThumbnailUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'block',
            width: '100%',
            aspectRatio: '800 / 580',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.2)',
            bgcolor: '#fff',
            position: 'relative',
            transition: 'transform 0.4s ease',
            '&:hover': {
              transform: 'scale(1.02) translateY(-5px)',
            }
          }}
        >
          <Box
            component="img"
            src={currentAd.adSpaceImageUrl}
            alt="Sponsored"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: '#fff',
              px: 1.2,
              py: 0.5,
              borderRadius: '8px',
              fontSize: '0.7rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              backdropFilter: 'blur(4px)',
              letterSpacing: 0.5
            }}
          >
            Ad
          </Box>

          {ads.length > 1 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0,0,0,0.3)',
                backdropFilter: 'blur(4px)',
                px: 1.5,
                py: 0.5,
                borderRadius: '20px',
                zIndex: 2
              }}
            >
              {ads.map((_, idx) => (
                <CircleIcon
                  key={idx}
                  sx={{
                    fontSize: 8,
                    color: idx === currentIndex ? '#fff' : 'rgba(255,255,255,0.4)',
                    transition: 'all 0.3s ease',
                    transform: idx === currentIndex ? 'scale(1.2)' : 'scale(1)'
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Fade>
    </Box>
  );
};

// --- 3. MAIN SECTION ---
const DealerListSection = memo(({ dealerData, title = 'Our Partner Dealers', adSpaceList = [] }: Props) => {
  const navigate = useNavigate();

  if (!dealerData || dealerData.length === 0) return null;

  const dealerAds = adSpaceList ? adSpaceList.filter(ad => ad.adPositionCode === 'homepage-dealer') : [];
  const showAds = dealerAds.length > 0;

  const visibleDealers = dealerData
    .filter(dealer => dealer.showDealerCompanyLogo)
    .slice(0, 12);

  return (
    <Box sx={{ py: 8, px: { xs: 2, md: 0 } }} maxWidth="lg" mx="auto">
      {/* Header Area */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 5,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={600} sx={{ color: '#1a1a1a', letterSpacing: '-0.5px' }}>
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={0.5}>
            Browse vehicles from Singapore's most trusted dealers
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            width: { xs: '100%', md: 'auto' },
            bgcolor: '#fff',
            p: 0.8,
            borderRadius: '50px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}
        >
          <Autocomplete
            options={dealerData}
            getOptionLabel={(option) => option.dealerCompanyName}
            onChange={(_, value) => {
              if (value) {
                // ✅ ENCODED: Converts id 15 -> "/dealer-vehicles/X9k2m"
                navigate(`/dealer-vehicles/${encodeId(value.dealerCompanyId)}`);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search dealer..."
                variant="standard"
                InputProps={{ ...params.InputProps, disableUnderline: true }}
                sx={{ px: 2, minWidth: 220 }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  component="img"
                  src={option.imageUrl}
                  alt={option.dealerCompanyName}
                  sx={{ width: 24, height: 24, objectFit: 'contain', mr: 1.5 }}
                />
                {option.dealerCompanyName}
              </li>
            )}
            sx={{ flexGrow: 1 }}
          />

          <Button
            variant="contained"
            onClick={() => navigate('/dealer-vehicles')}
            sx={{
              borderRadius: '30px',
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              px: 4,
              bgcolor: '#ffaa00',
              color: 'white',
              '&:hover': { bgcolor: '#FFC933', color: 'black', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
            }}
          >
            View All
          </Button>
        </Box>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={5} alignItems="flex-start">
        <Grid size={{ xs: 12, md: showAds ? 7 : 12 }}>
          <Grid container spacing={2}>
            {visibleDealers.map((dealer) => (
              <DealerCard key={dealer.dealerCompanyId} dealer={dealer} />
            ))}
          </Grid>
        </Grid>

        {showAds && (
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ position: { md: 'sticky' }, top: 24 }}>
              <DealerSideAd ads={dealerAds} />
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
});

export default DealerListSection;